<template>
  <div v-if="show" class="confirm-modal-overlay" @click="handleOverlayClick">
    <div class="confirm-modal-content" @click.stop>
      <div class="confirm-modal-header">
        <div class="confirm-modal-icon">
          <i :class="iconClass"></i>
        </div>
        <h3 class="confirm-modal-title">{{ title }}</h3>
      </div>
      
      <div class="confirm-modal-body">
        <p class="confirm-modal-message">{{ message }}</p>
        <div v-if="details" class="confirm-modal-details">
          <div v-for="(detail, key) in details" :key="key" class="detail-item">
            <span class="detail-label">{{ key }}:</span>
            <span class="detail-value">{{ detail }}</span>
          </div>
        </div>
      </div>
      
      <div class="confirm-modal-footer">
        <button 
          class="btn-cancel" 
          @click="handleCancel"
          :disabled="loading"
        >
          <i class="fa-solid fa-times"></i>
          {{ cancelText }}
        </button>
        <button 
          class="btn-confirm" 
          @click="handleConfirm"
          :disabled="loading"
          :class="[`btn-${type}`]"
        >
          <i v-if="loading" class="fa-solid fa-spinner fa-spin"></i>
          <i v-else :class="confirmIconClass"></i>
          {{ loading ? loadingText : confirmText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ConfirmModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'Confirmação'
    },
    message: {
      type: String,
      required: true
    },
    details: {
      type: Object,
      default: null
    },
    type: {
      type: String,
      default: 'primary', // primary, success, warning, danger
      validator: value => ['primary', 'success', 'warning', 'danger'].includes(value)
    },
    confirmText: {
      type: String,
      default: 'Confirmar'
    },
    cancelText: {
      type: String,
      default: 'Cancelar'
    },
    loadingText: {
      type: String,
      default: 'Processando...'
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    iconClass() {
      const iconMap = {
        primary: 'fa-solid fa-question-circle',
        success: 'fa-solid fa-check-circle',
        warning: 'fa-solid fa-exclamation-triangle',
        danger: 'fa-solid fa-exclamation-circle'
      };
      return iconMap[this.type];
    },
    confirmIconClass() {
      const iconMap = {
        primary: 'fa-solid fa-check',
        success: 'fa-solid fa-check',
        warning: 'fa-solid fa-exclamation',
        danger: 'fa-solid fa-trash'
      };
      return iconMap[this.type];
    }
  },
  methods: {
    handleConfirm() {
      this.$emit('confirm');
    },
    handleCancel() {
      this.$emit('cancel');
    },
    handleOverlayClick() {
      if (!this.loading) {
        this.handleCancel();
      }
    }
  }
};
</script>

<style scoped>
.confirm-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(2px);
}

.confirm-modal-content {
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  box-shadow: var(--shadow-lg);
  width: 90%;
  max-width: 480px;
  max-height: 90vh;
  overflow: hidden;
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.confirm-modal-header {
  padding: 2rem 2rem 1rem 2rem;
  text-align: center;
  border-bottom: 1px solid var(--border-color);
}

.confirm-modal-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem auto;
  font-size: 2rem;
}

.btn-primary .confirm-modal-icon {
  background: rgba(74, 144, 226, 0.1);
  color: var(--accent-color);
}

.btn-success .confirm-modal-icon {
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
}

.btn-warning .confirm-modal-icon {
  background: rgba(255, 193, 7, 0.1);
  color: #f59e0b;
}

.btn-danger .confirm-modal-icon {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.confirm-modal-title {
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.confirm-modal-body {
  padding: 1.5rem 2rem;
}

.confirm-modal-message {
  color: var(--text-secondary);
  font-size: 1rem;
  line-height: 1.6;
  margin: 0 0 1rem 0;
  text-align: center;
}

.confirm-modal-details {
  background: var(--secondary-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border-color);
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-label {
  color: var(--text-secondary);
  font-weight: 500;
  font-size: 0.9rem;
}

.detail-value {
  color: var(--text-primary);
  font-weight: 600;
  font-family: 'Consolas', 'Monaco', monospace;
}

.confirm-modal-footer {
  padding: 1.5rem 2rem 2rem 2rem;
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.btn-cancel,
.btn-confirm {
  padding: 0.875rem 2rem;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  min-width: 120px;
  justify-content: center;
}

.btn-cancel {
  background: var(--secondary-color);
  color: var(--text-primary);
  border: 2px solid var(--border-color);
}

.btn-cancel:hover:not(:disabled) {
  background: var(--border-color);
  transform: translateY(-1px);
}

.btn-confirm {
  color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.btn-confirm:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

.btn-primary {
  background: var(--accent-color);
}

.btn-primary:hover:not(:disabled) {
  background: var(--accent-hover);
}

.btn-success {
  background: #10b981;
}

.btn-success:hover:not(:disabled) {
  background: #059669;
}

.btn-warning {
  background: #f59e0b;
}

.btn-warning:hover:not(:disabled) {
  background: #d97706;
}

.btn-danger {
  background: #ef4444;
}

.btn-danger:hover:not(:disabled) {
  background: #dc2626;
}

.btn-cancel:disabled,
.btn-confirm:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Responsividade */
@media (max-width: 640px) {
  .confirm-modal-content {
    width: 95%;
    margin: 1rem;
  }
  
  .confirm-modal-header,
  .confirm-modal-body,
  .confirm-modal-footer {
    padding: 1.5rem 1rem;
  }
  
  .confirm-modal-footer {
    flex-direction: column;
  }
  
  .btn-cancel,
  .btn-confirm {
    width: 100%;
  }
}
</style> 